import React, { useCallback, useEffect, useMemo } from "react";

import { isAxiosError } from "axios";
import { FormProvider, useForm } from "react-hook-form";

import {
  type GoalBasedExpensePeriod,
  GoalBasedFinancialPlanExpenseDeleteResponse,
  GoalBasedFinancialPlanExpensePatchRequest,
  GoalBasedFinancialPlanExpensePostRequest,
  GoalBasedFinancialPlanExpenseResponse,
} from "@fartherfinance/frontend/api/FinancialPlans/expenses/types";
import type { GoalBasedFinancialPlanId } from "@fartherfinance/frontend/api/FinancialPlans/ids";
import { GoalBasedFinancialPlanRetirementHorizonResponse } from "@fartherfinance/frontend/api/FinancialPlans/retirementHorizon/types";
import {
  GoalBasedRepeatTimeFrame,
  GoalBasedRepeatTimeFrameLabel,
  GoalBasedRepeatTimeFrameSchema,
} from "@fartherfinance/frontend/api/FinancialPlans/types";

import { AddedAccountOption } from "../../Assets/Types";
import {
  AlreadyStartedOption,
  ApproachField,
  AutoTransportField,
  BillsUtilitiesField,
  BusinessField,
  CharitableGivingField,
  defaultPlanDateFieldValues,
  DiningOutField,
  EducationField,
  EndOfYourPlanningHorizonOption,
  EndsField,
  EntertainmentField,
  ExpensePeriodField,
  FeesChargesField,
  GiftField,
  GroceriesField,
  HealthFitnessField,
  KidsField,
  MiscField,
  NameField,
  OwnerField,
  PaymentAccountField,
  RentField,
  RepeatsField,
  ShoppingField,
  StartsField,
  TargetAmountField,
  TravelField,
  UncategorizedField,
  YourRetirementOption,
} from "../../Shared/constants";
import { formatAnnualIncrease } from "../../Shared/helpers/formatAnnualIncrease";
import { generatePlanDateFromFormValues } from "../../Shared/helpers/generatePlanDateFromFormValues";
import { getFromAccountFromOptions } from "../../Shared/helpers/getFromAccountFromOptions";
import { populateLivingExpenseFormFields } from "../../Shared/helpers/populateLivingExpenseFormFields";
import AnnualIncreaseMultiDropdownInput, {
  AnnualIncreaseAmountField,
  AnnualIncreaseFields,
  AnnualIncreaseTypeField,
  defaultAnnualIncreaseFieldValues,
} from "../../Shared/MultiDropdown/AnnualIncrease/AnnualIncrease";
import RepeatsMultiDropdownInput from "../../Shared/MultiDropdown/Repeats/Repeats";
import {
  OwnerFieldType,
  RepeatDropdownFormFieldTypes,
} from "../../Shared/Types";
import {
  approachOptions,
  DeleteParams,
  PatchParams,
  PickExpensesFormFields,
  PostParams,
} from "../Types";
import FormDrawer from "@src/multiCustodian/components/FinancialPlans/CreateUpdatePlanFlow/pages/Shared/FormDrawer/FormDrawer";
import { ErrorMessage } from "@src/multiCustodian/components/TaxBudgets/reducer/types";
import useStatusNotification from "@src/multiCustodian/hooks/useStatusNotification";
import AlertInline from "@src/sharedComponents/AlertInline/AlertInline";
import FlexWrapper from "@src/sharedComponents/Forms/FlexWrapper";
import FormDropdownField from "@src/sharedComponents/Forms/FormDropdownField";
import FormNumberField from "@src/sharedComponents/Forms/FormNumberField";
import FormTextField from "@src/sharedComponents/Forms/FormTextField";
import removeCommas from "@src/utils/CommaHelperFunctions/removeCommas";
import removeCommasUnd from "@src/utils/CommaHelperFunctions/removeCommasUnd";
import {
  formNumberFieldWithCommasCustomRegex,
  formNumberFieldWithCommasRules,
} from "@src/utils/formValidation";

import styles from "../Expenses.module.css";

type PickedFormFields = PickExpensesFormFields<
  | "OwnerField"
  | "NameField"
  | "TargetAmountField"
  | "PaymentAccountField"
  | "ExpensePeriodField"
  | "ApproachField"
  | "AutoTransportField"
  | "BillsUtilitiesField"
  | "BusinessField"
  | "CharitableGivingField"
  | "DiningOutField"
  | "EducationField"
  | "EntertainmentField"
  | "FeesChargesField"
  | "GiftField"
  | "GroceriesField"
  | "HealthFitnessField"
  | "KidsField"
  | "MiscField"
  | "RentField"
  | "ShoppingField"
  | "TravelField"
  | "UncategorizedField"
>;

type Form = PickedFormFields &
  AnnualIncreaseFields &
  RepeatDropdownFormFieldTypes;

interface Props {
  deleteExpense: (
    params: DeleteParams
  ) => Promise<GoalBasedFinancialPlanExpenseDeleteResponse>;
  drawerOpen: boolean;
  expensePeriod: GoalBasedExpensePeriod;
  expenseToEdit?: GoalBasedFinancialPlanExpenseResponse;
  goalBasedFinancialPlanId: GoalBasedFinancialPlanId;
  isLoading: boolean;
  onClose: () => void;
  owner: OwnerFieldType;
  patchExpense: (
    params: PatchParams
  ) => Promise<GoalBasedFinancialPlanExpenseResponse>;
  postExpense: (
    params: PostParams
  ) => Promise<GoalBasedFinancialPlanExpenseResponse>;
  retirementHorizons: GoalBasedFinancialPlanRetirementHorizonResponse[];
  addedAccountOptions: AddedAccountOption[];
  hideSpouseOptions: boolean;
}

const nameExpensePeriod = (expensePeriod: GoalBasedExpensePeriod): string => {
  switch (expensePeriod) {
    case "AfterRetirement":
      return "After Retirement";
    case "BeforeRetirement":
      return "Before Retirement";
    case "Other":
      return "";
  }
};

const LivingExpenseForm: React.FC<Props> = ({
  deleteExpense,
  drawerOpen,
  expensePeriod,
  expenseToEdit,
  goalBasedFinancialPlanId,
  isLoading,
  onClose,
  owner,
  patchExpense,
  postExpense,
  retirementHorizons,
  addedAccountOptions,
  hideSpouseOptions,
}) => {
  const statusNotification = useStatusNotification();
  const defaultValues = useMemo(
    () => ({
      [OwnerField]: {
        label: hideSpouseOptions ? owner.label : "Shared",
        value: owner.value,
      },
      [NameField]: `Living Expenses ${nameExpensePeriod(expensePeriod)}`,
      [TargetAmountField]: "0",
      [PaymentAccountField]: getFromAccountFromOptions(
        expenseToEdit?.paymentAccountId,
        addedAccountOptions
      ),
      [ExpensePeriodField]: expensePeriod,
      [ApproachField]: { label: "Simple", value: "SimpleLiving" },
      [AutoTransportField]: "0",
      [BillsUtilitiesField]: "0",
      [BusinessField]: "0",
      [CharitableGivingField]: "0",
      [DiningOutField]: "0",
      [EducationField]: "0",
      [EntertainmentField]: "0",
      [FeesChargesField]: "0",
      [GiftField]: "0",
      [GroceriesField]: "0",
      [HealthFitnessField]: "0",
      [KidsField]: "0",
      [MiscField]: "0",
      [RentField]: "0",
      [ShoppingField]: "0",
      [TravelField]: "0",
      [UncategorizedField]: "0",
      ...defaultPlanDateFieldValues,
      ...defaultAnnualIncreaseFieldValues,
      [RepeatsField]: {
        label: "Every Month" as GoalBasedRepeatTimeFrameLabel,
        value: "EveryMonth" as GoalBasedRepeatTimeFrame,
      },
      [StartsField]:
        expensePeriod === "BeforeRetirement"
          ? AlreadyStartedOption
          : YourRetirementOption,
      [EndsField]:
        expensePeriod === "BeforeRetirement"
          ? YourRetirementOption
          : EndOfYourPlanningHorizonOption,
    }),
    [
      expensePeriod,
      owner.value,
      owner.label,
      addedAccountOptions,
      expenseToEdit,
      hideSpouseOptions,
    ]
  );

  const methods = useForm<Form>({
    mode: "all",
    reValidateMode: "onChange",
    defaultValues,
  });

  const {
    formState: { isDirty, isValid },
    getValues,
    reset,
    watch,
  } = methods;

  const repeatsValue = watch(RepeatsField);
  const startsValue = watch(StartsField);
  const endsValue = watch(EndsField);

  const setFormFields = useCallback(() => {
    if (drawerOpen) {
      if (expenseToEdit !== undefined) {
        reset({
          [PaymentAccountField]: getFromAccountFromOptions(
            expenseToEdit?.paymentAccountId,
            addedAccountOptions
          ),
          ...populateLivingExpenseFormFields({ expense: expenseToEdit }),
          [OwnerField]: {
            label: hideSpouseOptions ? owner.label : "Shared",
            value: owner.value,
          },
          [NameField]: `Living Expenses ${nameExpensePeriod(expensePeriod)}`,
          ...defaultPlanDateFieldValues,
          [RepeatsField]: { label: "Every Month", value: "EveryMonth" },
          [StartsField]:
            expensePeriod === "BeforeRetirement"
              ? {
                  label: "Already Started",
                  value: "AlreadyStarted",
                  dateType: "AlreadyStarted",
                }
              : {
                  label: "Your Retirement",
                  value: "YourRetirement",
                  dateType: "Age",
                },
          [EndsField]:
            expensePeriod === "BeforeRetirement"
              ? {
                  label: "Your Retirement",
                  value: "YourRetirement",
                  dateType: "Age",
                }
              : {
                  label: "End of Your Planning Horizon",
                  value: "EndOfYourPlanningHorizon",
                  dateType: "Age",
                },
        });
      } else {
        reset({ ...defaultValues });
      }
    }
  }, [
    drawerOpen,
    defaultValues,
    expensePeriod,
    expenseToEdit,
    reset,
    addedAccountOptions,
    owner,
    hideSpouseOptions,
  ]);

  useEffect(() => {
    setFormFields();
  }, [setFormFields]);

  const handleOnClose = () => {
    setFormFields();
    onClose();
  };

  const approach = watch(ApproachField);

  const annualIncreaseTypeValue = watch(AnnualIncreaseTypeField);

  const onSave = async () => {
    const values = getValues();

    if (values[PaymentAccountField] === undefined) {
      return;
    }

    if (expensePeriod === "Other") {
      throw new Error("Expense Period cannot be other");
    }

    const amount = removeCommas(values[TargetAmountField]);

    if (approach.value === "SimpleLiving" && amount === undefined) {
      throw new Error("Amount required for simple living");
    }

    const startDate =
      expenseToEdit === undefined || expenseToEdit?.startDate === null
        ? generatePlanDateFromFormValues({
            clientId: owner.value,
            value:
              expensePeriod === "BeforeRetirement"
                ? "AlreadyStarted"
                : "YourRetirement",
            retirementHorizons,
            repeats: values[RepeatsField].value,
          })
        : expenseToEdit.startDate;

    const endDate =
      expenseToEdit === undefined || expenseToEdit?.endDate === null
        ? generatePlanDateFromFormValues({
            clientId: owner.value,
            value:
              expensePeriod === "BeforeRetirement"
                ? "YourRetirement"
                : "EndOfYourPlanningHorizon",
            retirementHorizons,
            repeats: values[RepeatsField].value,
          })
        : expenseToEdit.endDate;

    const annualIncrease = formatAnnualIncrease(
      values[AnnualIncreaseTypeField].value,
      values[AnnualIncreaseAmountField]
    );

    if (expenseToEdit === undefined) {
      const body: GoalBasedFinancialPlanExpensePostRequest =
        approach.value === "SimpleLiving"
          ? {
              owner: owner.value,
              name: values[NameField],
              expenseType: "SimpleLiving",
              amount,
              period: expensePeriod,
              paymentAccountId: values[PaymentAccountField]?.value,
              annualIncrease,
              startDate,
              endDate,
              repeats: GoalBasedRepeatTimeFrameSchema.Enum.EveryMonth,
            }
          : {
              livingExpenses: {
                autoTransport: removeCommasUnd(values[AutoTransportField]),
                billsUtilities: removeCommasUnd(values[BillsUtilitiesField]),
                businessExpenses: removeCommasUnd(values[BusinessField]),
                charitableGiving: removeCommasUnd(
                  values[CharitableGivingField]
                ),
                diningOut: removeCommasUnd(values[DiningOutField]),
                education: removeCommasUnd(values[EducationField]),
                entertainment: removeCommasUnd(values[EntertainmentField]),
                feesCharges: removeCommasUnd(values[FeesChargesField]),
                gifts: removeCommasUnd(values[GiftField]),
                groceries: removeCommasUnd(values[GroceriesField]),
                healthFitness: removeCommasUnd(values[HealthFitnessField]),
                kids: removeCommasUnd(values[KidsField]),
                miscellaneous: removeCommasUnd(values[MiscField]),
                rent: removeCommasUnd(values[RentField]),
                shopping: removeCommasUnd(values[ShoppingField]),
                travel: removeCommasUnd(values[TravelField]),
                uncategorized: removeCommasUnd(values[UncategorizedField]),
              },
              owner: owner.value,
              name: values[NameField],
              expenseType: "DetailedLiving",
              period: expensePeriod,
              paymentAccountId: values[PaymentAccountField]?.value,
              annualIncrease,
              startDate,
              endDate,
              repeats: GoalBasedRepeatTimeFrameSchema.Enum.EveryMonth,
            };

      try {
        await postExpense({ body, goalBasedFinancialPlanId });

        statusNotification("Expense added", "Success");

        handleOnClose();
      } catch (err) {
        if (isAxiosError<ErrorMessage>(err)) {
          const message = err.response?.data.message;
          statusNotification(`Error adding expense: ${message ?? ""}`, "Error");
        } else {
          statusNotification(
            "There was an error adding the expense. Please try again.",
            "Error"
          );
        }
      }
    }

    if (expenseToEdit !== undefined) {
      const body: GoalBasedFinancialPlanExpensePatchRequest =
        approach.value === "SimpleLiving"
          ? {
              owner: owner.value,
              name: values[NameField],
              expenseType: "SimpleLiving",
              amount,
              period: expensePeriod,
              paymentAccountId: values[PaymentAccountField]?.value,
              annualIncrease,
              startDate,
              endDate,
              repeats: GoalBasedRepeatTimeFrameSchema.Enum.EveryMonth,
            }
          : {
              livingExpenses: {
                autoTransport: removeCommasUnd(values[AutoTransportField]),
                billsUtilities: removeCommasUnd(values[BillsUtilitiesField]),
                businessExpenses: removeCommasUnd(values[BusinessField]),
                charitableGiving: removeCommasUnd(
                  values[CharitableGivingField]
                ),
                diningOut: removeCommasUnd(values[DiningOutField]),
                education: removeCommasUnd(values[EducationField]),
                entertainment: removeCommasUnd(values[EntertainmentField]),
                feesCharges: removeCommasUnd(values[FeesChargesField]),
                gifts: removeCommasUnd(values[GiftField]),
                groceries: removeCommasUnd(values[GroceriesField]),
                healthFitness: removeCommasUnd(values[HealthFitnessField]),
                kids: removeCommasUnd(values[KidsField]),
                miscellaneous: removeCommasUnd(values[MiscField]),
                rent: removeCommasUnd(values[RentField]),
                shopping: removeCommasUnd(values[ShoppingField]),
                travel: removeCommasUnd(values[TravelField]),
                uncategorized: removeCommasUnd(values[UncategorizedField]),
              },
              owner: owner.value,
              name: values[NameField],
              expenseType: "DetailedLiving",
              period: expensePeriod,
              paymentAccountId: values[PaymentAccountField]?.value,
              annualIncrease,
              startDate,
              endDate,
              repeats: GoalBasedRepeatTimeFrameSchema.Enum.EveryMonth,
            };

      try {
        await patchExpense({
          body,
          goalBasedFinancialPlanId,
          goalBasedFinancialPlanExpenseId: expenseToEdit.expenseId,
        });

        statusNotification("Expense added", "Success");

        handleOnClose();
      } catch (err) {
        if (isAxiosError<ErrorMessage>(err)) {
          const message = err.response?.data.message;
          statusNotification(`Error adding expense: ${message ?? ""}`, "Error");
        } else {
          statusNotification(
            "There was an error adding the expense. Please try again.",
            "Error"
          );
        }
      }
    }
  };

  const onRemove = async () => {
    if (expenseToEdit === undefined) {
      throw new Error("No expense to remove");
    }

    try {
      await deleteExpense({
        goalBasedFinancialPlanId,
        goalBasedFinancialPlanExpenseId: expenseToEdit.expenseId,
      });

      statusNotification("Expense deleted", "Success");

      handleOnClose();
    } catch (err) {
      if (isAxiosError<ErrorMessage>(err)) {
        const message = err.response?.data.message;
        statusNotification(`Error deleting expense: ${message ?? ""}`, "Error");
      } else {
        statusNotification(
          "There was an error deleting the expense. Please try again.",
          "Error"
        );
      }
    }
  };

  return (
    <FormDrawer
      title={`Living ${hideSpouseOptions ? "Expense" : "Expenses"}`}
      subTitle={
        expensePeriod === "AfterRetirement"
          ? "After Retirement"
          : "Before Retirement"
      }
      drawerOpen={drawerOpen}
      onClose={handleOnClose}
      saveDisabled={isLoading || !isValid || !isDirty}
      onSave={onSave}
      removeDisabled={isLoading || expenseToEdit === undefined}
      onRemove={onRemove}
      isLoading={isLoading}
      hasError={false}
      deleteModalTitle={"Remove Expense"}
      deleteModalSubTitle={"Are you sure you want to remove this expense?"}
    >
      <AlertInline
        className={styles.alert}
        message={
          "Do not include any debt payments (e.g. mortgage) in living expenses."
        }
        severity={"info"}
      />

      <FormProvider {...methods}>
        <FlexWrapper>
          <FormTextField
            name={NameField}
            label="Name"
            required={"Must not be empty"}
            disabled
          />

          <FormDropdownField
            name={OwnerField}
            label={"Owner"}
            values={[
              {
                label: hideSpouseOptions ? owner.label : "Shared",
                value: owner.value,
              },
            ]}
            disableSearch={true}
            required={"Must not be empty"}
          />
        </FlexWrapper>

        <FlexWrapper>
          <FormNumberField
            name={TargetAmountField}
            label="Monthly Amount"
            disabled={approach.label === "Detailed Worksheet"}
            startAdornment={"$"}
            customRegex={formNumberFieldWithCommasCustomRegex}
            rules={formNumberFieldWithCommasRules({
              required: true,
              minValue: 0,
              maxValue: 1_000_000,
            })}
            autoAddCommas={true}
          />

          <FormDropdownField
            name={PaymentAccountField}
            label="Payment Account"
            values={addedAccountOptions}
            disableSearch={true}
            required={"Must not be empty"}
          />
        </FlexWrapper>

        <FlexWrapper>
          <AnnualIncreaseMultiDropdownInput
            fieldLabel="Annual Increase"
            additionalInputFieldLabel="Custom Annual Inflation"
            curOption={annualIncreaseTypeValue?.value}
            isDisabled={false}
          />

          <FormDropdownField
            name={ApproachField}
            label="Approach"
            values={approachOptions}
            disableSearch={true}
          />
        </FlexWrapper>

        {approach.value === "DetailedLiving" && (
          <>
            <FlexWrapper>
              <FormNumberField
                name={AutoTransportField}
                label="Auto & Transport"
                startAdornment={"$"}
                customRegex={formNumberFieldWithCommasCustomRegex}
                rules={formNumberFieldWithCommasRules({
                  required: true,
                  minValue: 0,
                  maxValue: 1_000_000,
                })}
                autoAddCommas={true}
              />

              <FormNumberField
                name={BillsUtilitiesField}
                label="Bills & Utilities"
                startAdornment={"$"}
                customRegex={formNumberFieldWithCommasCustomRegex}
                rules={formNumberFieldWithCommasRules({
                  required: true,
                  minValue: 0,
                  maxValue: 1_000_000,
                })}
                autoAddCommas={true}
              />
            </FlexWrapper>

            <FlexWrapper>
              <FormNumberField
                name={BusinessField}
                label="Business"
                startAdornment={"$"}
                customRegex={formNumberFieldWithCommasCustomRegex}
                rules={formNumberFieldWithCommasRules({
                  required: true,
                  minValue: 0,
                  maxValue: 1_000_000,
                })}
                autoAddCommas={true}
              />

              <FormNumberField
                name={CharitableGivingField}
                label="Charitable Giving"
                startAdornment={"$"}
                customRegex={formNumberFieldWithCommasCustomRegex}
                rules={formNumberFieldWithCommasRules({
                  required: true,
                  minValue: 0,
                  maxValue: 1_000_000,
                })}
                autoAddCommas={true}
              />
            </FlexWrapper>

            <FlexWrapper>
              <FormNumberField
                name={DiningOutField}
                label="Dining Out"
                startAdornment={"$"}
                customRegex={formNumberFieldWithCommasCustomRegex}
                rules={formNumberFieldWithCommasRules({
                  required: true,
                  minValue: 0,
                  maxValue: 1_000_000,
                })}
                autoAddCommas={true}
              />

              <FormNumberField
                name={EducationField}
                label="Education"
                startAdornment={"$"}
                customRegex={formNumberFieldWithCommasCustomRegex}
                rules={formNumberFieldWithCommasRules({
                  required: true,
                  minValue: 0,
                  maxValue: 1_000_000,
                })}
                autoAddCommas={true}
              />
            </FlexWrapper>

            <FlexWrapper>
              <FormNumberField
                name={EntertainmentField}
                label="Entertainment"
                startAdornment={"$"}
              />

              <FormNumberField
                name={FeesChargesField}
                label="Fees & Charges"
                startAdornment={"$"}
                customRegex={formNumberFieldWithCommasCustomRegex}
                rules={formNumberFieldWithCommasRules({
                  required: true,
                  minValue: 0,
                  maxValue: 1_000_000,
                })}
                autoAddCommas={true}
              />
            </FlexWrapper>

            <FlexWrapper>
              <FormNumberField
                name={GiftField}
                label="Gift"
                startAdornment={"$"}
                customRegex={formNumberFieldWithCommasCustomRegex}
                rules={formNumberFieldWithCommasRules({
                  required: true,
                  minValue: 0,
                  maxValue: 1_000_000,
                })}
                autoAddCommas={true}
              />

              <FormNumberField
                name={GroceriesField}
                label="Groceries"
                startAdornment={"$"}
                customRegex={formNumberFieldWithCommasCustomRegex}
                rules={formNumberFieldWithCommasRules({
                  required: true,
                  minValue: 0,
                  maxValue: 1_000_000,
                })}
                autoAddCommas={true}
              />
            </FlexWrapper>

            <FlexWrapper>
              <FormNumberField
                name={HealthFitnessField}
                label="Health & Fitness"
                startAdornment={"$"}
                customRegex={formNumberFieldWithCommasCustomRegex}
                rules={formNumberFieldWithCommasRules({
                  required: true,
                  minValue: 0,
                  maxValue: 1_000_000,
                })}
                autoAddCommas={true}
              />

              <FormNumberField
                name={KidsField}
                label="Kids"
                startAdornment={"$"}
                customRegex={formNumberFieldWithCommasCustomRegex}
                rules={formNumberFieldWithCommasRules({
                  required: true,
                  minValue: 0,
                  maxValue: 1_000_000,
                })}
                autoAddCommas={true}
              />
            </FlexWrapper>

            <FlexWrapper>
              <FormNumberField
                name={MiscField}
                label="Miscellaneous"
                startAdornment={"$"}
                customRegex={formNumberFieldWithCommasCustomRegex}
                rules={formNumberFieldWithCommasRules({
                  required: true,
                  minValue: 0,
                  maxValue: 1_000_000,
                })}
                autoAddCommas={true}
              />

              <FormNumberField
                name={RentField}
                label="Rent"
                startAdornment={"$"}
                customRegex={formNumberFieldWithCommasCustomRegex}
                rules={formNumberFieldWithCommasRules({
                  required: true,
                  minValue: 0,
                  maxValue: 100_000_000,
                })}
                autoAddCommas={true}
              />
            </FlexWrapper>

            <FlexWrapper>
              <FormNumberField
                name={TravelField}
                label="Travel"
                startAdornment={"$"}
                customRegex={formNumberFieldWithCommasCustomRegex}
                rules={formNumberFieldWithCommasRules({
                  required: true,
                  minValue: 0,
                  maxValue: 1_000_000,
                })}
                autoAddCommas={true}
              />

              <FormNumberField
                name={ShoppingField}
                label="Shopping"
                startAdornment={"$"}
                customRegex={formNumberFieldWithCommasCustomRegex}
                rules={formNumberFieldWithCommasRules({
                  required: true,
                  minValue: 0,
                  maxValue: 1_000_000,
                })}
                autoAddCommas={true}
              />
            </FlexWrapper>

            <FlexWrapper>
              <FormNumberField
                name={UncategorizedField}
                label="Uncategorized"
                startAdornment={"$"}
                customRegex={formNumberFieldWithCommasCustomRegex}
                rules={formNumberFieldWithCommasRules({
                  required: true,
                  minValue: 0,
                  maxValue: 1_000_000,
                })}
                autoAddCommas={true}
              />
            </FlexWrapper>
          </>
        )}

        <FlexWrapper>
          <RepeatsMultiDropdownInput
            isDisabled
            curRepeatsOption={repeatsValue.value}
            curPlanDateStartOption={startsValue.value}
            curPlanDateEndOption={endsValue.value}
            hideSpouseOptions={hideSpouseOptions}
          />
        </FlexWrapper>
      </FormProvider>
    </FormDrawer>
  );
};

export default LivingExpenseForm;
